/**
 * One hour of the Day timeline's chart: a thin column (≤ 24 px, 4 px rounded
 * top, a 2 px gap between containers, the first container at the bottom)
 * whose height is the hour's drops or SMS against the axis; the whole hour
 * is the hover and focus target, and a closed hour sits on a muted band.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import { instantDk } from '../../../utils/clock/dk-label';
import type { HourBucket } from './day-hours';
import { HourTip } from './HourTip';

export type DayMetric = 'drops' | 'sms';

export const valueOf = (x: { drops: number; sms: number }, metric: DayMetric) => (metric === 'drops' ? x.drops : x.sms);

export const HourColumn: FC<{ hour: HourBucket; metric: DayMetric; max: number }> = ({ hour, metric, max }) => {
  const total = valueOf(hour, metric);
  const label = `${instantDk(hour.startIso).label} Danish time: ${hour.drops} drops, ${hour.sms.toLocaleString('en-US')} SMS${hour.open ? '' : ', window closed'}`;
  return (
    <Tooltip className="flex h-full w-full" content={total > 0 || !hour.open ? <HourTip hour={hour} /> : null}>
      <span
        tabIndex={total > 0 ? 0 : -1}
        aria-label={label}
        className={cn('group flex h-full w-full items-end justify-center outline-none transition-colors hover:bg-primary/[0.06] focus-visible:bg-primary/10', !hour.open && 'bg-muted/50')}
        data-testid="day-hour"
      >
        {total > 0 ? (
          <span
            className="flex w-[min(24px,62%)] flex-col-reverse gap-[2px] overflow-hidden rounded-t-[4px] transition-[height,opacity] duration-500 ease-out group-hover:opacity-85 motion-reduce:transition-none"
            style={{ height: `${(total / max) * 100}%` }}
          >
            {hour.slices.map((s) =>
              valueOf(s, metric) > 0 ? <span key={s.containerId} className="block min-h-[2px]" style={{ flexGrow: valueOf(s, metric), flexBasis: 0, backgroundColor: s.color }} /> : null,
            )}
          </span>
        ) : null}
      </span>
    </Tooltip>
  );
};
